import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { RefreshCw, CreditCard, CheckCircle2, XCircle, Clock, ExternalLink, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  paid: { icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50 border-emerald-200", label: "Pagato" },
  failed: { icon: XCircle, cls: "text-rose-600 bg-rose-50 border-rose-200", label: "Fallito" },
  pending: { icon: Clock, cls: "text-amber-600 bg-amber-50 border-amber-200", label: "In attesa" },
};

function fmtEur(cents) {
  if (cents == null) return "—";
  return "€ " + (cents / 100).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function NexiTransactionsTab() {
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");

  const fetchTxs = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.get(`${API}/nexi/transactions?limit=200`, { withCredentials: true });
      setTxs(data?.transactions || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore caricamento transazioni");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTxs(); }, [fetchTxs]);

  const filtered = filter === "all" ? txs : txs.filter(t => t.status === filter);

  const stats = {
    total: txs.length,
    paid: txs.filter(t => t.status === "paid").length,
    pending: txs.filter(t => t.status === "pending").length,
    failed: txs.filter(t => t.status === "failed").length,
    incassato: txs.filter(t => t.status === "paid").reduce((s, t) => s + (t.amount_cents || 0), 0),
  };

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden" data-testid="nexi-tab">
      <div className="p-5 border-b border-zinc-200 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-amber-50 border border-amber-200 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="font-bold text-lg">Transazioni Nexi XPay</h2>
            <p className="text-xs text-zinc-500">Pagamenti polizze tramite circuito Nexi</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="https://int-ecommerce.nexi.it/ecomm/web/reporting/ReportLogin.jsp"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-900 text-white text-xs font-medium hover:bg-zinc-800"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Backoffice Nexi
          </a>
          <button
            onClick={fetchTxs}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-zinc-200 text-zinc-700 text-xs font-medium hover:bg-zinc-50 disabled:opacity-60"
            data-testid="nexi-refresh-btn"
          >
            <RefreshCw className={"w-3.5 h-3.5 " + (loading ? "animate-spin" : "")} />
            Aggiorna
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-5 border-b border-zinc-100 bg-zinc-50/50">
        <Stat label="Totali" value={stats.total} />
        <Stat label="Pagate" value={stats.paid} tone="emerald" />
        <Stat label="In attesa" value={stats.pending} tone="amber" />
        <Stat label="Fallite" value={stats.failed} tone="rose" />
        <Stat label="Incassato" value={fmtEur(stats.incassato)} tone="emerald" wide />
      </div>

      {/* Filter */}
      <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2">
        {["all", "paid", "pending", "failed"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={
              "px-3 py-1 rounded-full text-xs font-medium transition-colors " +
              (filter === f ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200")
            }
            data-testid={`nexi-filter-${f}`}
          >
            {f === "all" ? "Tutte" : f === "paid" ? "Pagate" : f === "pending" ? "In attesa" : "Fallite"}
          </button>
        ))}
      </div>

      {err && <div className="p-4 text-rose-600 text-sm bg-rose-50 border-b border-rose-100">{err}</div>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Data</th>
              <th className="px-4 py-3 text-left">Cliente</th>
              <th className="px-4 py-3 text-left">Prodotto</th>
              <th className="px-4 py-3 text-left">Contratto</th>
              <th className="px-4 py-3 text-right">Importo</th>
              <th className="px-4 py-3 text-left">Stato</th>
              <th className="px-4 py-3 text-left">Cod. Trans.</th>
            </tr>
          </thead>
          <tbody>
            {loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-zinc-400">Caricamento…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-zinc-400">Nessuna transazione</td></tr>
            )}
            {filtered.map((tx) => {
              const st = STATUS_STYLES[tx.status] || STATUS_STYLES.pending;
              const Ico = st.icon;
              return (
                <tr key={tx.cod_trans} className="border-b border-zinc-50 hover:bg-zinc-50/50">
                  <td className="px-4 py-3 text-zinc-600 text-xs">{fmtDate(tx.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-zinc-900">{tx.customer_name || "—"}</div>
                    {tx.email && (
                      <a href={`mailto:${tx.email}`} className="text-xs text-zinc-500 hover:text-zinc-700 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {tx.email}
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-md bg-zinc-100 text-zinc-700 capitalize">
                      {tx.tipo_polizza || "generico"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-zinc-600">{tx.num_contratto || "—"}</td>
                  <td className="px-4 py-3 text-right font-semibold text-zinc-900">
                    {fmtEur(tx.amount_cents)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={"inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border " + st.cls}>
                      <Ico className="w-3 h-3" />
                      {st.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-zinc-400 break-all max-w-[160px]">
                    {tx.cod_trans}
                    {tx.cod_aut && <div className="text-[10px] text-zinc-300">aut: {tx.cod_aut}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "zinc", wide }) {
  const toneCls = {
    zinc: "text-zinc-900",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
  }[tone];
  return (
    <div className={"bg-white rounded-xl border border-zinc-200 px-4 py-3 " + (wide ? "col-span-2 md:col-span-1" : "")}>
      <p className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={"mt-0.5 text-xl font-bold " + toneCls}>{value}</p>
    </div>
  );
}
